import { useState, useEffect, useRef } from "react";
import * as Y from "yjs";
import { invoke } from "../lib/bridge";
import { pushListItem } from "../lib/listItem";
import { useYDoc } from "../hooks/useYDoc";
import { DocMeta } from "../App";
import { DeleteButton } from "./DeleteButton";
import { Button } from "./ui/Button";
import { DecryptedAvatar } from "./DecryptedAvatar";
import { DecryptedImage } from "./DecryptedImage";
import { linkify } from "./linkify";
import { ConfirmDialog } from "./ui/ConfirmDialog";
import { Modal } from "./ui/Modal";
import { Spinner } from "./ui/Spinner";
import { EmptyState } from "./ui/EmptyState";
import { PageHeader } from "./ui/PageHeader";

interface ListViewProps {
  doc: DocMeta;
  memberId: number;
  syncVersion?: number;
  onBack: () => void;
  onOpenItem: (itemId: string) => void;
}

interface ListItem {
  id: string;
  name: string;
  checked: boolean;
  createdBy?: number;
  photoUrl?: string;
  description?: string;
  checkedAt?: number;
}

// Checked items auto-hide (presentation-only, never a CRDT write) this long
// after they were checked. Mirrors the shape of TodayTab's DONE_TTL_MS but
// with a much shorter window: 24h, not 7 days.
const HIDE_TTL_MS = 24 * 60 * 60 * 1000;

function showHiddenStorageKey(docId: string): string {
  return `list-show-hidden:${docId}`;
}

interface MemberInfo {
  id: number;
  displayName: string;
  avatarUrl: string | null;
}

export function ListView({ doc, memberId, syncVersion, onBack, onOpenItem }: ListViewProps) {
  const { doc: yDoc, loading, error } = useYDoc(doc.docId, syncVersion);
  const [newItem, setNewItem] = useState("");
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; name: string } | null>(null);
  const [members, setMembers] = useState<MemberInfo[]>([]);
  const [uploadingFor, setUploadingFor] = useState<string | null>(null);
  const [viewingPhoto, setViewingPhoto] = useState<{ id: string; name: string; url: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingItemIdRef = useRef<string | null>(null);
  // Breakpoint for narrow-screen layout (mirrors the CSS media query).
  const NARROW_BREAKPOINT = "(max-width: 767px)";
  const [narrow, setNarrow] = useState(() => window.matchMedia(NARROW_BREAKPOINT).matches);
  // Device-local "show hidden" preference, keyed by docId and persisted to
  // localStorage (not synced via the Yjs doc; see hooks/useTheme.tsx for the
  // established pattern of a plain localStorage-backed toggle).
  const [showHidden, setShowHiddenState] = useState(
    () => localStorage.getItem(showHiddenStorageKey(doc.docId)) === "true"
  );
  // Auto-hide is computed at read time; re-evaluate roughly once a minute so a
  // mounted list notices items crossing the 24h boundary.
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    invoke<MemberInfo[]>("list_members")
      .then(setMembers)
      .catch(() => {});
  }, []);

  useEffect(() => {
    const mq = window.matchMedia(NARROW_BREAKPOINT);
    const onChange = (e: MediaQueryListEvent) => setNarrow(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    setShowHiddenState(localStorage.getItem(showHiddenStorageKey(doc.docId)) === "true");
  }, [doc.docId]);

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const setShowHidden = (value: boolean) => {
    localStorage.setItem(showHiddenStorageKey(doc.docId), String(value));
    setShowHiddenState(value);
  };

  const memberMap = new Map(members.map((m) => [m.id, m]));

  if (loading) return <div className="container"><Spinner /></div>;
  if (error) return <div className="container"><p>Error: {error}</p></div>;
  if (!yDoc) return null;

  const yItems = yDoc.getArray("items");
  const items = yItems.toJSON() as ListItem[];

  // Sorting/filtering the rendered array (below) means the map() index no
  // longer lines up with the Y.Array; every mutation must look items up by
  // id instead (mirrors TodayTab.findIndex / ListItemView.findItem).
  const findIndexById = (id: string): number => {
    for (let i = 0; i < yItems.length; i++) {
      const yItem = yItems.get(i) as Y.Map<unknown>;
      if (yItem.get("id") === id) return i;
    }
    return -1;
  };

  const isHidden = (item: ListItem): boolean =>
    item.checked && typeof item.checkedAt === "number" && now - item.checkedAt > HIDE_TTL_MS;

  const uncheckedItems = items.filter((item) => !item.checked);
  const checkedItems = items.filter((item) => item.checked);
  const checkedVisible = checkedItems.filter((item) => !isHidden(item));
  const checkedHidden = checkedItems.filter(isHidden);
  const hiddenCount = checkedHidden.length;
  const displayedItems = [...uncheckedItems, ...checkedVisible, ...(showHidden ? checkedHidden : [])];

  const addItem = () => {
    const name = newItem.trim();
    if (!name) return;
    pushListItem(yDoc, yItems, name, memberId);
    setNewItem("");
  };

  const toggleItem = (id: string) => {
    const index = findIndexById(id);
    if (index === -1) return;
    yDoc.transact(() => {
      const item = yItems.get(index) as Y.Map<unknown>;
      const wasChecked = item.get("checked");
      item.set("checked", !wasChecked);
      if (!wasChecked) {
        item.set("checkedAt", Date.now());
      } else {
        item.delete("checkedAt");
      }
    });
  };

  const deleteItem = (id: string) => {
    const index = findIndexById(id);
    if (index === -1) return;
    yDoc.transact(() => {
      yItems.delete(index, 1);
    });
  };

  const triggerPhotoUpload = (itemId: string) => {
    pendingItemIdRef.current = itemId;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const itemId = pendingItemIdRef.current;
    if (!file || !itemId) return;

    setUploadingFor(itemId);
    try {
      const result = await invoke<{ photoUrl: string; thumbUrl: string }>("upload_photo", { file });

      // Find the Y.Map item and set photoUrl
      for (let i = 0; i < yItems.length; i++) {
        const yItem = yItems.get(i) as Y.Map<unknown>;
        if (yItem.get("id") === itemId) {
          yDoc.transact(() => {
            yItem.set("photoUrl", result.thumbUrl);
          });
          break;
        }
      }
    } catch (err) {
      console.error("Failed to upload photo:", err);
    }
    setUploadingFor(null);
    pendingItemIdRef.current = null;
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const deletePhoto = (id: string) => {
    const index = findIndexById(id);
    if (index === -1) return;
    const yItem = yItems.get(index) as Y.Map<unknown>;
    yDoc.transact(() => {
      yItem.delete("photoUrl");
    });
    setViewingPhoto(null);
  };

  return (
    <div className="container">
      <PageHeader title={doc.title} onClose={onBack} />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          addItem();
        }}
        className="add-form"
      >
        <input
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          placeholder="Add item..."
          className="add-input"
        />
        <Button type="submit">Add</Button>
      </form>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelected}
        style={{ display: "none" }}
      />

      <ul className="item-list">
        {displayedItems.map((item) => {
          const creator = item.createdBy ? memberMap.get(item.createdBy) : undefined;
          const isUploading = uploadingFor === item.id;
          return (
            <li key={item.id} className="list-item">
              <input
                type="checkbox"
                checked={item.checked}
                onChange={() => toggleItem(item.id)}
                className="checkbox"
                aria-label={item.checked ? `Uncheck ${item.name}` : `Check ${item.name}`}
              />
              <div className="list-item-content">
                <span
                  className={`list-item-text ${item.checked ? "checked" : ""}`}
                  onClick={() => onOpenItem(item.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onOpenItem(item.id);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  style={{
                    cursor: "pointer",
                    ...(narrow
                      ? { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "18ch" }
                      : {}),
                  }}
                >
                  {linkify(item.name)}
                </span>
                {item.photoUrl ? (
                  <DecryptedAvatar
                    token={item.photoUrl}
                    name={item.name}
                    size="tiny"
                    onClick={() => setViewingPhoto({ id: item.id, name: item.name, url: item.photoUrl! })}
                  />
                ) : (
                  <button
                    className="btn-item-camera"
                    onClick={() => triggerPhotoUpload(item.id)}
                    disabled={isUploading}
                  >
                    {isUploading ? (
                      <span style={{ fontSize: "10px" }}>...</span>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="currentColor">
                        <path d="M480-260q75 0 127.5-52.5T660-440q0-75-52.5-127.5T480-620q-75 0-127.5 52.5T300-440q0 75 52.5 127.5T480-260Zm0-80q-42 0-71-29t-29-71q0-42 29-71t71-29q42 0 71 29t29 71q0 42-29 71t-71 29ZM160-120q-33 0-56.5-23.5T80-200v-480q0-33 23.5-56.5T160-760h126l74-80h240l74 80h126q33 0 56.5 23.5T880-680v480q0 33-23.5 56.5T800-120H160Zm0-80h640v-480H638l-73-80H395l-73 80H160v480Zm320-240Z"/>
                      </svg>
                    )}
                  </button>
                )}
                {creator && (
                  <DecryptedAvatar token={creator.avatarUrl} name={creator.displayName} size="tiny" />
                )}
              </div>
              <DeleteButton onClick={() => setConfirmDelete({ id: item.id, name: item.name })} />
            </li>
          );
        })}
      </ul>

      {items.length === 0 && (
        <EmptyState>No items yet. Add something above.</EmptyState>
      )}

      {hiddenCount > 0 && (
        <button
          type="button"
          className="show-hidden-toggle"
          onClick={() => setShowHidden(!showHidden)}
        >
          {showHidden ? "Hide" : `Show ${hiddenCount} hidden`}
        </button>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        message={confirmDelete ? `Delete "${confirmDelete.name}"?` : ""}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={() => {
          if (confirmDelete) {
            deleteItem(confirmDelete.id);
            setConfirmDelete(null);
          }
        }}
        onCancel={() => setConfirmDelete(null)}
      />

      <Modal
        open={!!viewingPhoto}
        onClose={() => setViewingPhoto(null)}
        title={viewingPhoto ? viewingPhoto.name : "Photo"}
        maxWidth="360px"
      >
        {viewingPhoto && (
          <>
            <div className="header-row" style={{ marginBottom: "12px" }}>
              <h3 style={{ margin: 0 }}>{viewingPhoto.name}</h3>
              <button onClick={() => setViewingPhoto(null)} className="btn-close">&times;</button>
            </div>
            <DecryptedImage
              token={viewingPhoto.url}
              alt={viewingPhoto.name}
              style={{ width: "100%", borderRadius: "12px", marginBottom: "12px" }}
            />
            <div className="confirm-actions">
              <button
                onClick={() => setViewingPhoto(null)}
                className="btn-secondary"
              >
                Close
              </button>
              <button
                onClick={() => deletePhoto(viewingPhoto.id)}
                className="btn-danger"
              >
                Delete Photo
              </button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
